import { useEffect, useState } from 'react';

import type { BankAccountOption } from './useContractPaymentData';
import type { PaymentRow } from './usePaymentRows';
import type { ContractApiResponse } from '../../services/contract-services';

/**
 * Flags whether any payment row's selected bank account country disagrees with its contract's
 * country (treating "USA" and "United States" as equivalent).
 */
export const useCountryMismatchCheck = (
    paymentRows: PaymentRow[],
    bankAccountOptions: BankAccountOption[],
    getContractById: (contractId: string) => ContractApiResponse | null,
) => {
    const [hasCountryMismatch, setHasCountryMismatch] = useState(false);

    const paymentRowsKey = JSON.stringify(paymentRows);
    const bankAccountOptionsKey = JSON.stringify(bankAccountOptions);

    useEffect(() => {
        let mismatch = false;
        for (const row of paymentRows) {
            if (!row.bankAccount) continue;
            const contract = getContractById(row.contractId) || getContractById(row.dbContractId);
            const bankAccount = bankAccountOptions.find(acc => acc.id === row.bankAccount);
            if (contract && bankAccount && contract.Country && bankAccount.country) {
                // Normalize country names for comparison
                const contractCountry = String(contract.Country).trim().toLowerCase();
                const bankCountry = String(bankAccount.country).trim().toLowerCase();
                // Consider 'usa' and 'united states' as equivalent
                const isContractUSA = contractCountry === 'usa' || contractCountry === 'united states';
                const isBankUSA = bankCountry === 'usa' || bankCountry === 'united states';
                if (
                    (isContractUSA && !isBankUSA) ||
                    (!isContractUSA && isBankUSA) ||
                    (!isContractUSA && !isBankUSA && contractCountry !== bankCountry)
                ) {
                    mismatch = true;
                    break;
                }
            }
        }
        setHasCountryMismatch(mismatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentRowsKey, bankAccountOptionsKey, getContractById]);

    return hasCountryMismatch;
};
